import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { createPortal } from 'react-dom'
import { Globe, ChevronDown, CheckCircle } from 'lucide-react'
import { LanguageCode, LANGUAGE_OPTIONS, DEFAULT_LANGUAGE } from '../config/language'
import { getApiUrl } from '../config/api'
import { useTopLayerPortal } from '../utils/top-layer-portal'

function useIsMobile(breakpoint = 480) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  )
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [breakpoint])
  return isMobile
}

interface PreSessionScreenProps {
  primaryColor: string
  onReady: (language: LanguageCode) => void
  onClose?: () => void
}

/**
 * PreSessionScreen — Mini pre-session card (deferred contact form mode).
 * Shows language picker + tips + consent. No email/phone fields.
 * Used when contact_form_timing = 'before_scheduling'.
 */
export const PreSessionScreen: React.FC<PreSessionScreenProps> = ({ primaryColor, onReady, onClose }) => {
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(DEFAULT_LANGUAGE)
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false)
  const [consentChecked, setConsentChecked] = useState(false)
  const isMobile = useIsMobile()
  const portalTarget = useTopLayerPortal()

  const handleStart = () => {
    onReady(selectedLanguage)
  }

  const isValid = consentChecked

  if (!portalTarget) {
    return null
  }

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 2147483647,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? 8 : 16,
        pointerEvents: 'auto',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        style={{
          maxWidth: 420,
          width: '100%',
          maxHeight: '95vh',
          overflowY: 'auto',
          backgroundColor: 'white',
          borderRadius: 16,
          padding: isMobile ? 18 : 32,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          direction: 'rtl',
          background: 'linear-gradient(135deg, #ffffff 0%, #fef9f3 100%)',
          position: 'relative',
        }}
      >
        {/* Close button */}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 12,
              left: 12,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#9ca3af',
              fontSize: 20,
              lineHeight: 1,
              padding: 4,
            }}
          >
            ✕
          </button>
        )}

        {/* Language Picker */}
        <div style={{ marginBottom: isMobile ? 14 : 20, position: 'relative' }}>
          <label htmlFor="presession-language-button" style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 600, color: '#374151' }}>
            <Globe size={16} style={{ display: 'inline', verticalAlign: 'middle', marginLeft: 6 }} />
            שפת השיחה
          </label>
          <button
            id="presession-language-button"
            type="button"
            onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 8,
              border: `2px solid ${primaryColor}40`,
              backgroundColor: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: 15,
              color: '#374151',
              transition: 'all 0.2s',
              animation: 'pulse-glow 2s ease-in-out infinite',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>
                {LANGUAGE_OPTIONS.find(l => l.code === selectedLanguage)?.flag || '🌐'}
              </span>
              <span>{LANGUAGE_OPTIONS.find(l => l.code === selectedLanguage)?.label || 'בחר שפה'}</span>
            </span>
            <ChevronDown size={18} style={{ color: '#9ca3af', transform: isLanguageDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
          </button>
          {isLanguageDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: 4,
                background: 'white',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                zIndex: 100,
                overflow: 'hidden',
              }}
            >
              {LANGUAGE_OPTIONS.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => {
                    setSelectedLanguage(lang.code)
                    setIsLanguageDropdownOpen(false)
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    backgroundColor: selectedLanguage === lang.code ? `${primaryColor}15` : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontSize: 14,
                    color: '#374151',
                    transition: 'background-color 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    if (selectedLanguage !== lang.code) e.currentTarget.style.backgroundColor = '#f9fafb'
                  }}
                  onMouseLeave={(e) => {
                    if (selectedLanguage !== lang.code) e.currentTarget.style.backgroundColor = 'white'
                  }}
                >
                  <span style={{ fontSize: 18 }}>{lang.flag}</span>
                  <span>{lang.label}</span>
                  {selectedLanguage === lang.code && (
                    <CheckCircle size={16} style={{ marginRight: 'auto', marginLeft: 8, color: primaryColor }} />
                  )}
                </button>
              ))}
            </div>
          )}
          <p style={{ margin: '6px 0 0', fontSize: 12, color: '#9ca3af' }}>
            השיחה תתנהל בשפה שנבחרה
          </p>
        </div>

        {/* Tips Grid */}
        <div style={{ textAlign: 'center', marginBottom: isMobile ? 10 : 16 }}>
          <div style={{ fontSize: isMobile ? 20 : 24, marginBottom: 4 }}>💡</div>
          <h3 style={{ margin: 0, fontSize: isMobile ? 15 : 16, fontWeight: 700, color: '#1f2937' }}>
            טיפים לשיחה מוצלחת
          </h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr', gap: isMobile ? 8 : 12, marginBottom: 20 }}>
          <TipCard compact={isMobile} iconType="image" iconSrc={getApiUrl('/InteruputM.png')} title="הפסקת הסוכנת באמצע" description="הסוכנת תפסיק לדבר כשתתחילו" />
          <TipCard compact={isMobile} iconType="image" iconSrc={getApiUrl('/MuteMIC.png')} title="השתקת מיקרופון" description="מכבה את המיקרופון שלכם" />
          <TipCard compact={isMobile} icon="🤫" title="סביבה שקטה" description="מקום רועש? הסוכנ\ת עלולה להתבלבל" />
          <TipCard compact={isMobile} icon="📝" title="התמליל לא מדויק?" description="תתעלמו - הסוכנ\ת מבינ\ה אתכם מצוין" />
        </div>

        {/* Consent */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              style={{ width: 18, height: 18, marginTop: 2, accentColor: primaryColor, cursor: 'pointer', flexShrink: 0 }}
            />
            <span style={{ direction: 'rtl', textAlign: 'right' }}>
              אני מאשר/ת שימוש במידע לצורך יצירת קשר, וידוע לי שהשיחה עשויה להיות מוקלטת ומנותחת
            </span>
          </label>
        </div>

        {/* Keyframe animation */}
        <style>{`
          @keyframes pulse-glow {
            0%, 100% { box-shadow: 0 0 8px ${primaryColor}30; }
            50% { box-shadow: 0 0 20px ${primaryColor}50; }
          }
        `}</style>

        {/* Start Button */}
        <button
          type="button"
          onClick={handleStart}
          disabled={!isValid}
          style={{
            width: '100%',
            padding: '14px 24px',
            borderRadius: 8,
            border: 'none',
            background: isValid
              ? `linear-gradient(135deg, ${primaryColor}, ${adjustBrightness(primaryColor, 0.8)})`
              : '#e5e7eb',
            color: isValid ? 'white' : '#9ca3af',
            fontSize: 16,
            fontWeight: 600,
            cursor: isValid ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s',
            opacity: isValid ? 1 : 0.6,
            boxShadow: isValid ? `0 4px 12px ${primaryColor}40` : 'none',
          }}
          onMouseEnter={(e) => {
            if (isValid) {
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow = `0 6px 16px ${primaryColor}60`
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = isValid ? `0 4px 12px ${primaryColor}40` : 'none'
          }}
        >
          שנתחיל?
        </button>
      </motion.div>
    </motion.div>,
    portalTarget
  )
}

// Reused TipCard from ContactForm
const TipCard: React.FC<{
  icon?: string
  iconType?: 'emoji' | 'image'
  iconSrc?: string
  title: string
  description: string
  compact?: boolean
}> = ({ icon, iconType = 'emoji', iconSrc, title, description, compact = false }) => (
  <div
    style={{
      padding: compact ? '8px 6px' : '10px',
      borderRadius: 8,
      backgroundColor: 'white',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
      transition: 'all 0.2s',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)'
      e.currentTarget.style.transform = 'translateY(-2px)'
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)'
      e.currentTarget.style.transform = 'translateY(0)'
    }}
  >
    <div style={{ marginBottom: 4, textAlign: 'center', height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {iconType === 'image' && iconSrc ? (
        <img src={iconSrc} alt={title} style={{ width: 22, height: 22, objectFit: 'contain' }} />
      ) : (
        <span style={{ fontSize: 18 }}>{icon}</span>
      )}
    </div>
    <div style={{ fontSize: compact ? 11 : 12, fontWeight: 600, color: '#1f2937', marginBottom: 2, textAlign: 'center', lineHeight: 1.3 }}>{title}</div>
    <div style={{ fontSize: compact ? 10 : 10, color: '#6b7280', lineHeight: 1.35, textAlign: 'center' }}>{description}</div>
  </div>
)

function adjustBrightness(color: string, factor: number): string {
  const hex = color.replace('#', '')
  const r = Math.round(parseInt(hex.substring(0, 2), 16) * factor)
  const g = Math.round(parseInt(hex.substring(2, 4), 16) * factor)
  const b = Math.round(parseInt(hex.substring(4, 6), 16) * factor)
  return '#' + [r, g, b].map(v => Math.min(255, v).toString(16).padStart(2, '0')).join('')
}
